import { useEffect, useState } from "react";
import API from "../services/api";
import { useNavigate } from "react-router-dom";

export default function Students() {

    const navigate = useNavigate();

    // =====================================================
    // STUDENTS
    // =====================================================

    const [students, setStudents] = useState([]);

    // =====================================================
    // SEARCH
    // =====================================================

    const [searchAdmission, setSearchAdmission] = useState("");
    const [searchName, setSearchName] = useState("");
    const [searchMobile, setSearchMobile] = useState("");
    const [searchClass, setSearchClass] = useState("");
    const [searchSection, setSearchSection] = useState("");

    // =====================================================
    // FILTERS
    // =====================================================

    const [classFilter, setClassFilter] = useState("");
    const [sectionFilter, setSectionFilter] = useState("");

    // Active / Inactive / All
    const [statusFilter, setStatusFilter] = useState("Active");

    // =====================================================
    // MODALS
    // =====================================================

    const [selectedStudent, setSelectedStudent] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    // =====================================================
    // FORM
    // =====================================================

    const [errors, setErrors] = useState({});

    const [form, setForm] = useState({
        name: "",
        dob: "",
        gender: "",
        className: "",
        section: "A",
        father_name: "",
        mother_name: "",
        guardian_name: "",
        parent_mobile: "",
        alternate_mobile: "",
        parent_email: "",
        emergency_contact: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        aadhaar_no: "",
        blood_group: "",
        admission_date: "",
        academic_year: "",
        status: "Active"
    });

    // =====================================================
    // PAGINATION / SORT
    // =====================================================

    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("admission");
    const [studentsPerPage, setStudentsPerPage] = useState(10);

    // =====================================================
    // CLASS LIST
    // =====================================================

    const classOptions = [
        "PP1",
        "PP2",
        "LKG",
        "UKG",
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "10",
        "11",
        "12"
    ];

    const sectionOptions =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

    // =====================================================
    // LOAD
    // =====================================================

    useEffect(() => {
        fetchStudents();
    }, []);

    // =====================================================
    // FETCH STUDENTS
    // =====================================================

    const fetchStudents = async () => {

        try {

            const res = await API.get("/students");

            setStudents(
                Array.isArray(res.data)
                    ? res.data
                    : []
            );

        } catch (err) {

            console.error(
                "FETCH STUDENTS ERROR:",
                err
            );

            alert(
                err.response?.data?.message ||
                "Unable to fetch students."
            );
        }
    };

    // =====================================================
    // ADD STUDENT
    // =====================================================

    const handleAddStudent = async () => {

        const newErrors = {};
        const missingFields = [];

        if (!form.name.trim()) {
            newErrors.name = true;
            missingFields.push("Student Name");
        }

        if (!form.dob) {
            newErrors.dob = true;
            missingFields.push("Date of Birth");
        }

        if (!form.gender) {
            newErrors.gender = true;
            missingFields.push("Gender");
        }

        if (!form.className) {
            newErrors.className = true;
            missingFields.push("Class");
        }

        if (!form.section) {
            newErrors.section = true;
            missingFields.push("Section");
        }

        setErrors(newErrors);

        if (missingFields.length > 0) {

            alert(
                `Please complete mandatory fields:\n\n• ${missingFields.join(
                    "\n• "
                )}`
            );

            return;
        }

        try {

            const payload = {
                ...form,
                status: "Active"
            };

            const res = await API.post(
                "/students/add",
                payload
            );

            alert(
                `🎉 Admission Completed Successfully\n\nAdmission Number: ${res.data.admission_no}`
            );

            setForm({
                name: "",
                dob: "",
                gender: "",
                className: "",
                section: "A",
                father_name: "",
                mother_name: "",
                guardian_name: "",
                parent_mobile: "",
                alternate_mobile: "",
                parent_email: "",
                emergency_contact: "",
                address: "",
                city: "",
                state: "",
                pincode: "",
                aadhaar_no: "",
                blood_group: "",
                admission_date: "",
                academic_year: "",
                status: "Active"
            });

            setErrors({});

            await fetchStudents();

        } catch (err) {

            console.error(
                "ADD STUDENT ERROR:",
                err
            );

            alert(
                err.response?.data?.msg ||
                err.response?.data?.message ||
                err.message ||
                "Unable to add student."
            );
        }
    };

    // =====================================================
    // ACTIVATE / DEACTIVATE
    // =====================================================

    const handleDeleteStudent = async (student) => {

        const isInactive =
            student.status === "Inactive";

        const action = isInactive
            ? "activate"
            : "deactivate";

        const confirmAction = window.confirm(
            `Are you sure you want to ${action} "${student.name}"?`
        );

        if (!confirmAction) {
            return;
        }

        try {

            const res = await API.delete(
                `/students/delete/${student.id}`
            );

            alert(
                res.data?.message ||
                (
                    isInactive
                        ? "Student activated successfully."
                        : "Student deactivated successfully."
                )
            );

            await fetchStudents();

        } catch (err) {

            console.error(
                "CHANGE STUDENT STATUS ERROR:",
                err
            );

            console.error(
                "SERVER RESPONSE:",
                err.response?.data
            );

            alert(
                err.response?.data?.message ||
                "Unable to change student status."
            );
        }
    };

    // =====================================================
    // UPDATE STUDENT
    // =====================================================

    const handleUpdateStudent = async () => {

        try {

            if (!selectedStudent?.id) {

                alert(
                    "Student information is missing."
                );

                return;
            }

            if (
                !selectedStudent.name ||
                !selectedStudent.name.trim()
            ) {

                alert(
                    "Student name is required."
                );

                return;
            }

            const payload = {

                name: selectedStudent.name,

                dob: selectedStudent.dob,

                gender: selectedStudent.gender,

                className: selectedStudent.class,

                section: selectedStudent.section,

                father_name:
                    selectedStudent.father_name,

                mother_name:
                    selectedStudent.mother_name,

                parent_mobile:
                    selectedStudent.parent_mobile,

                emergency_contact:
                    selectedStudent.emergency_contact,

                address:
                    selectedStudent.address,

                status:
                    selectedStudent.status === "Inactive"
                        ? "Inactive"
                        : "Active"
            };

            await API.put(
                `/students/update/${selectedStudent.id}`,
                payload
            );

            alert(
                "Student updated successfully."
            );

            setShowEditModal(false);

            setSelectedStudent(null);

            await fetchStudents();

        } catch (err) {

            console.error(
                "UPDATE STUDENT ERROR:",
                err
            );

            alert(
                err.response?.data?.message ||
                "Unable to update student."
            );
        }
    };

    // =====================================================
    // FILTER STUDENTS
    // =====================================================

    const filteredStudents =
        students.filter((student) => {

            const studentStatus =
                student.status || "Active";

            return (

                // Admission
                (
                    student.admission_no
                        ?.toLowerCase()
                        .includes(
                            searchAdmission
                                .toLowerCase()
                        )
                )

                &&

                // Name
                (
                    student.name
                        ?.toLowerCase()
                        .includes(
                            searchName
                                .toLowerCase()
                        )
                )

                &&

                // Mobile
                (
                    student.parent_mobile
                        ?.toLowerCase()
                        .includes(
                            searchMobile
                                .toLowerCase()
                        )
                )

                &&

                // Class
                (
                    searchClass === "" ||
                    student.class === searchClass
                )

                &&

                // Section
                (
                    searchSection === "" ||
                    student.section === searchSection
                )

                &&

                // Status
                (
                    statusFilter === "All" ||
                    studentStatus === statusFilter
                )

                &&

                // Extra class filter
                (
                    classFilter === "" ||
                    student.class === classFilter
                )

                &&

                // Extra section filter
                (
                    sectionFilter === "" ||
                    student.section === sectionFilter
                )

            );

        });

    // =====================================================
    // SORT
    // =====================================================

    const sortedStudents =
        [...filteredStudents];

    switch (sortBy) {

        case "name":

            sortedStudents.sort(
                (a, b) =>
                    (a.name || "").localeCompare(
                        b.name || ""
                    )
            );

            break;

        case "class":

            sortedStudents.sort(
                (a, b) => {

                    if (
                        a.class === b.class
                    ) {

                        return (
                            a.section || ""
                        ).localeCompare(
                            b.section || ""
                        );
                    }

                    return (
                        a.class || ""
                    ).localeCompare(
                        b.class || ""
                    );
                }
            );

            break;

        case "balance":

            sortedStudents.sort(
                (a, b) =>
                    Number(b.balance || 0) -
                    Number(a.balance || 0)
            );

            break;

        default:

            sortedStudents.sort(
                (a, b) =>
                    (
                        a.admission_no || ""
                    ).localeCompare(
                        b.admission_no || ""
                    )
            );
    }

    // =====================================================
    // PAGINATION
    // =====================================================

    const indexOfLastStudent =
        currentPage * studentsPerPage;

    const indexOfFirstStudent =
        indexOfLastStudent -
        studentsPerPage;

    const currentStudents =
        sortedStudents.slice(
            indexOfFirstStudent,
            indexOfLastStudent
        );

    const totalPages =
        Math.ceil(
            filteredStudents.length /
            studentsPerPage
        );

    // =====================================================
    // SUMMARY
    // =====================================================

    const activeStudents =
        students.filter(
            (s) =>
                (s.status || "Active") ===
                "Active"
        ).length;

    const inactiveStudents =
        students.filter(
            (s) =>
                s.status === "Inactive"
        ).length;

    const fullyPaid =
        students.filter(
            (s) =>
                s.fee_status === "Paid"
        ).length;

    const partialStudents =
        students.filter(
            (s) =>
                s.fee_status === "Partial"
        ).length;

    const dueStudents =
        students.filter(
            (s) =>
                s.fee_status === "Not Started"
        ).length;

    const outstandingAmount =
        students.reduce(
            (sum, s) =>
                sum +
                Number(s.balance || 0),
            0
        );

    // =====================================================
    // RETURN
    // =====================================================

    return (

        <div className="p-3 sm:p-4 lg:p-6 bg-gray-100 min-h-screen">

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="mb-6 lg:mb-8">

                <div>

                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800">
                        Student Directory
                    </h1>

                    <p className="text-gray-500 mt-2">
                        View, search and manage student records
                    </p>

                </div>

            </div>


            {/* =================================================
                SUMMARY CARDS
            ================================================= */}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-5 mb-6 lg:mb-8">

                {/* Total */}

                <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl shadow-lg p-5">

                    <p className="text-sm opacity-90">
                        Total Students
                    </p>

                    <h2 className="text-3xl font-bold mt-2">
                        {students.length}
                    </h2>

                </div>


                {/* Active */}

                <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-2xl shadow-lg p-5">

                    <p className="text-sm opacity-90">
                        Active
                    </p>

                    <h2 className="text-3xl font-bold mt-2">
                        {activeStudents}
                    </h2>

                </div>


                {/* Inactive */}

                <div className="bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-2xl shadow-lg p-5">

                    <p className="text-sm opacity-90">
                        Inactive
                    </p>

                    <h2 className="text-3xl font-bold mt-2">
                        {inactiveStudents}
                    </h2>

                </div>


                {/* Fully Paid */}

                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-2xl shadow-lg p-5">

                    <p className="text-sm opacity-90">
                        Fully Paid
                    </p>

                    <h2 className="text-3xl font-bold mt-2">
                        {fullyPaid}
                    </h2>

                </div>


                {/* Partial */}

                <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-2xl shadow-lg p-5">

                    <p className="text-sm opacity-90">
                        Partial
                    </p>

                    <h2 className="text-3xl font-bold mt-2">
                        {partialStudents}
                    </h2>

                </div>


                {/* Due */}

                <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl shadow-lg p-5">

                    <p className="text-sm opacity-90">
                        Payment Due
                    </p>

                    <h2 className="text-3xl font-bold mt-2">
                        {dueStudents}
                    </h2>

                </div>

            </div>


            {/* =================================================
                DIRECTORY
            ================================================= */}

            <div className="bg-white rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8">

                {/* Header */}

                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-8">

                    <div>

                        <h2 className="text-3xl font-bold">
                            Student Directory
                        </h2>

                        <p className="text-gray-500 mt-1">
                            Manage all student records
                        </p>

                    </div>

                </div>


                {/* =================================================
                    SEARCH
                ================================================= */}

                <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6 border">

                    <div className="flex items-center justify-between mb-5">

                        <div>

                            <h2 className="text-2xl font-bold text-gray-800">
                                Search Students
                            </h2>

                            <p className="text-gray-500">
                                Search by admission number, name,
                                mobile, class or section
                            </p>

                        </div>

                    </div>


                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">

                        {/* Admission */}

                        <input
                            type="text"
                            placeholder="Admission No"
                            value={searchAdmission}
                            onChange={(e) => {
                                setSearchAdmission(
                                    e.target.value
                                );
                                setCurrentPage(1);
                            }}
                            className="border rounded-xl p-3 focus:ring-2 focus:ring-blue-500 outline-none"
                        />


                        {/* Student */}

                        <input
                            type="text"
                            placeholder="Student Name"
                            value={searchName}
                            onChange={(e) => {
                                setSearchName(
                                    e.target.value
                                );
                                setCurrentPage(1);
                            }}
                            className="border rounded-xl p-3 focus:ring-2 focus:ring-blue-500 outline-none"
                        />


                        {/* Parent Mobile */}

                        <input
                            type="text"
                            placeholder="Parent Mobile"
                            value={searchMobile}
                            onChange={(e) => {
                                setSearchMobile(
                                    e.target.value
                                );
                                setCurrentPage(1);
                            }}
                            className="border rounded-xl p-3 focus:ring-2 focus:ring-blue-500 outline-none"
                        />


                        {/* Class */}

                        <select
                            value={searchClass}
                            onChange={(e) => {
                                setSearchClass(
                                    e.target.value
                                );
                                setCurrentPage(1);
                            }}
                            className="border rounded-xl p-3"
                        >

                            <option value="">
                                All Classes
                            </option>

                            {classOptions.map(
                                (cls) => (

                                    <option
                                        key={cls}
                                        value={cls}
                                    >
                                        {cls}
                                    </option>

                                )
                            )}

                        </select>


                        {/* Section */}

                        <select
                            value={searchSection}
                            onChange={(e) => {
                                setSearchSection(
                                    e.target.value
                                );
                                setCurrentPage(1);
                            }}
                            className="border rounded-xl p-3"
                        >

                            <option value="">
                                All Sections
                            </option>

                            {sectionOptions.map(
                                (section) => (

                                    <option
                                        key={section}
                                        value={section}
                                    >
                                        {section}
                                    </option>

                                )
                            )}

                        </select>


                        {/* Status */}

                        <select
                            value={statusFilter}
                            onChange={(e) => {

                                setStatusFilter(
                                    e.target.value
                                );

                                setCurrentPage(1);

                            }}
                            className="border-2 border-green-300 rounded-xl p-3 font-semibold bg-white"
                        >

                            <option value="Active">
                                Active Students
                            </option>

                            <option value="Inactive">
                                Inactive Students
                            </option>

                            <option value="All">
                                All Students
                            </option>

                        </select>


                        {/* Reset */}

                        <button
                            onClick={() => {

                                setSearchAdmission("");
                                setSearchName("");
                                setSearchMobile("");
                                setSearchClass("");
                                setSearchSection("");
                                setClassFilter("");
                                setSectionFilter("");
                                setStatusFilter("Active");
                                setCurrentPage(1);

                            }}
                            className="bg-gray-800 hover:bg-gray-900 text-white rounded-xl px-4 py-3 font-semibold"
                        >
                            Reset
                        </button>

                    </div>

                </div>


                {/* =================================================
                    SECONDARY FILTERS
                ================================================= */}

                <div className="flex flex-col md:flex-row gap-4 mb-6">

                    <select
                        value={classFilter}
                        onChange={(e) => {

                            setClassFilter(
                                e.target.value
                            );

                            setCurrentPage(1);

                        }}
                        className="border rounded-2xl px-5 py-3"
                    >

                        <option value="">
                            All Classes
                        </option>

                        {classOptions.map(
                            (cls) => (

                                <option
                                    key={cls}
                                    value={cls}
                                >
                                    {cls}
                                </option>

                            )
                        )}

                    </select>


                    <select
                        value={sectionFilter}
                        onChange={(e) => {

                            setSectionFilter(
                                e.target.value
                            );

                            setCurrentPage(1);

                        }}
                        className="border rounded-2xl px-5 py-3"
                    >

                        <option value="">
                            All Sections
                        </option>

                        {sectionOptions.map(
                            (letter) => (

                                <option
                                    key={letter}
                                    value={letter}
                                >
                                    {letter}
                                </option>

                            )
                        )}

                    </select>

                </div>


                {/* =================================================
                    SORT / PAGINATION CONTROL
                ================================================= */}

                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-5">

                    <div className="flex flex-col lg:flex-row justify-between items-center gap-4">

                        <div className="flex flex-wrap items-center gap-4">

                            {/* Sort */}

                            <div>

                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Sort Students
                                </label>

                                <select
                                    value={sortBy}
                                    onChange={(e) => {

                                        setSortBy(
                                            e.target.value
                                        );

                                        setCurrentPage(1);

                                    }}
                                    className="border-2 border-blue-300 rounded-xl px-4 py-2 bg-white font-semibold"
                                >

                                    <option value="admission">
                                        Admission Number
                                    </option>

                                    <option value="name">
                                        Student Name (A-Z)
                                    </option>

                                    <option value="class">
                                        Class
                                    </option>

                                    <option value="balance">
                                        Highest Balance
                                    </option>

                                </select>

                            </div>


                            {/* Students Per Page */}

                            <div>

                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Students Per Page
                                </label>

                                <select
                                    value={studentsPerPage}
                                    onChange={(e) => {

                                        setStudentsPerPage(
                                            Number(
                                                e.target.value
                                            )
                                        );

                                        setCurrentPage(1);

                                    }}
                                    className="border-2 border-green-300 rounded-xl px-4 py-2 bg-white font-semibold"
                                >

                                    <option value={10}>
                                        10
                                    </option>

                                    <option value={25}>
                                        25
                                    </option>

                                    <option value={50}>
                                        50
                                    </option>

                                    <option value={100}>
                                        100
                                    </option>

                                </select>

                            </div>

                        </div>


                        {/* Showing */}

                        <div className="bg-white px-5 py-3 rounded-xl shadow font-semibold text-gray-700">

                            Showing{" "}

                            <span className="text-blue-700 mx-2">
                                {filteredStudents.length === 0
                                    ? 0
                                    : indexOfFirstStudent + 1}
                            </span>

                            -

                            <span className="text-blue-700 mx-2">
                                {Math.min(
                                    indexOfLastStudent,
                                    filteredStudents.length
                                )}
                            </span>

                            of

                            <span className="text-green-700 mx-2">
                                {filteredStudents.length}
                            </span>

                            Students

                        </div>

                    </div>

                </div>


                {/* =================================================
                    TABLE
                ================================================= */}

                <div className="overflow-x-auto">

                    <table className="w-full">

                        <thead>

                            <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">

                                <th className="p-4 text-left">
                                    Admission
                                </th>

                                <th className="p-4 text-left">
                                    Student
                                </th>

                                <th className="p-4 text-left">
                                    Class
                                </th>

                                <th className="p-4 text-left">
                                    Parent
                                </th>

                                <th className="p-4 text-center">
                                    Annual Fee
                                </th>

                                <th className="p-4 text-center">
                                    Paid
                                </th>

                                <th className="p-4 text-center">
                                    Balance
                                </th>

                                <th className="p-4 text-center">
                                    Fee Status
                                </th>

                                <th className="p-4 text-center">
                                    Student Status
                                </th>

                                <th className="p-4 text-center">
                                    Last Payment
                                </th>

                                <th className="p-4 text-center">
                                    Actions
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {currentStudents.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="11"
                                        className="text-center py-12 text-gray-500"
                                    >

                                        <div className="text-4xl mb-3">
                                            🎓
                                        </div>

                                        <p className="font-semibold text-lg">
                                            No students found
                                        </p>

                                        <p className="text-sm mt-1">
                                            Try changing your search
                                            or status filter.
                                        </p>

                                    </td>

                                </tr>

                            ) : (

                                currentStudents.map(
                                    (student) => (

                                        <tr
                                            key={student.id}
                                            className={`border-b hover:bg-blue-50 transition ${student.status ===
                                                    "Inactive"
                                                    ? "bg-gray-50"
                                                    : ""
                                                }`}
                                        >

                                            {/* Admission */}

                                            <td className="p-4 font-bold text-blue-700">

                                                {student.admission_no}

                                            </td>


                                            {/* Student */}

                                            <td className="p-4">

                                                <div className="font-semibold">

                                                    {student.name}

                                                </div>

                                                <div className="text-xs text-gray-500">

                                                    {student.gender || "-"}

                                                </div>

                                            </td>


                                            {/* Class */}

                                            <td className="p-4">

                                                <span className="font-semibold">

                                                    {student.class}
                                                    -
                                                    {student.section}

                                                </span>

                                            </td>


                                            {/* Parent */}

                                            <td className="p-4">

                                                <div>

                                                    {student.father_name ||
                                                        "-"}

                                                </div>

                                                <div className="text-xs text-gray-500">

                                                    {student.parent_mobile ||
                                                        "-"}

                                                </div>

                                            </td>


                                            {/* Annual Fee */}

                                            <td className="p-4 text-center font-semibold">

                                                ₹
                                                {Number(
                                                    student.annual_fee ||
                                                    0
                                                ).toLocaleString()}

                                            </td>


                                            {/* Paid */}

                                            <td className="p-4 text-center text-green-700 font-bold">

                                                ₹
                                                {Number(
                                                    student.total_paid ||
                                                    0
                                                ).toLocaleString()}

                                            </td>


                                            {/* Balance */}

                                            <td
                                                className={`p-4 text-center font-bold ${Number(
                                                    student.balance
                                                ) > 0
                                                        ? "text-red-600"
                                                        : "text-green-600"
                                                    }`}
                                            >

                                                ₹
                                                {Number(
                                                    student.balance ||
                                                    0
                                                ).toLocaleString()}

                                            </td>


                                            {/* Fee Status */}

                                            <td className="p-4 text-center">

                                                <span
                                                    className={`px-3 py-1 rounded-full text-xs font-bold ${student.fee_status ===
                                                            "Paid"
                                                            ? "bg-green-100 text-green-700"
                                                            : student.fee_status ===
                                                                "Partial"
                                                                ? "bg-yellow-100 text-yellow-700"
                                                                : "bg-red-100 text-red-700"
                                                        }`}
                                                >

                                                    {student.fee_status ||
                                                        "Not Started"}

                                                </span>

                                            </td>


                                            {/* Student Status */}

                                            <td className="p-4 text-center">

                                                <span
                                                    className={`px-3 py-1 rounded-full text-xs font-bold ${student.status ===
                                                            "Inactive"
                                                            ? "bg-gray-200 text-gray-700"
                                                            : "bg-green-100 text-green-700"
                                                        }`}
                                                >

                                                    {student.status ===
                                                        "Inactive"
                                                        ? "Inactive"
                                                        : "Active"}

                                                </span>

                                            </td>


                                            {/* Last Payment */}

                                            <td className="p-4 text-center text-sm">

                                                {student.last_payment
                                                    ? new Date(
                                                        student.last_payment
                                                    ).toLocaleDateString()
                                                    : "-"}

                                            </td>


                                            {/* Actions */}

                                            <td className="p-4">

                                                <div className="flex flex-wrap gap-2 justify-center">

                                                    {/* Fee */}

                                                    <button
                                                        onClick={() =>
                                                            navigate(
                                                                `/fees?student=${student.id}`
                                                            )
                                                        }
                                                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-sm"
                                                    >
                                                        💰 Fee
                                                    </button>


                                                    {/* History */}

                                                    <button
                                                        onClick={() =>
                                                            navigate(
                                                                `/fee-history?student=${student.id}`
                                                            )
                                                        }
                                                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-lg text-sm"
                                                    >
                                                        📜 History
                                                    </button>


                                                    {/* View */}

                                                    <button
                                                        onClick={() => {

                                                            setSelectedStudent(
                                                                student
                                                            );

                                                            setShowViewModal(
                                                                true
                                                            );

                                                        }}
                                                        className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg text-sm"
                                                    >
                                                        View
                                                    </button>


                                                    {/* Edit */}

                                                    <button
                                                        onClick={() => {

                                                            setSelectedStudent(
                                                                {
                                                                    ...student,

                                                                    dob:
                                                                        student.dob
                                                                            ? String(
                                                                                student.dob
                                                                            ).split(
                                                                                "T"
                                                                            )[0]
                                                                            : "",

                                                                    status:
                                                                        student.status ||
                                                                        "Active"
                                                                }
                                                            );

                                                            setShowEditModal(
                                                                true
                                                            );

                                                        }}
                                                        className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-lg text-sm"
                                                    >
                                                        Edit
                                                    </button>


                                                    {/* Activate / Deactivate */}

                                                    <button
                                                        onClick={() =>
                                                            handleDeleteStudent(
                                                                student
                                                            )
                                                        }
                                                        className={
                                                            student.status ===
                                                                "Inactive"
                                                                ? "bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1 rounded-lg text-sm font-semibold"
                                                                : "bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded-lg text-sm font-semibold"
                                                        }
                                                    >

                                                        {student.status ===
                                                            "Inactive"
                                                            ? "Activate"
                                                            : "Deactivate"}

                                                    </button>

                                                </div>

                                            </td>

                                        </tr>

                                    )
                                )

                            )}

                        </tbody>

                    </table>

                </div>


                {/* =================================================
                    PAGINATION
                ================================================= */}

                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">

                    <div className="flex items-center gap-3">

                        <span className="text-gray-600">
                            Show
                        </span>

                        <select
                            value={studentsPerPage}
                            onChange={(e) => {

                                setStudentsPerPage(
                                    Number(
                                        e.target.value
                                    )
                                );

                                setCurrentPage(1);

                            }}
                            className="border rounded-lg px-3 py-2"
                        >

                            <option value={10}>
                                10
                            </option>

                            <option value={25}>
                                25
                            </option>

                            <option value={50}>
                                50
                            </option>

                            <option value={100}>
                                100
                            </option>

                        </select>

                    </div>


                    <div className="flex items-center gap-2">

                        <button
                            disabled={
                                currentPage === 1
                            }
                            onClick={() =>
                                setCurrentPage(
                                    currentPage - 1
                                )
                            }
                            className="px-4 py-2 rounded-lg bg-gray-200 disabled:opacity-40"
                        >
                            Previous
                        </button>


                        <span className="font-semibold">

                            Page{" "}
                            {currentPage} of{" "}
                            {totalPages || 1}

                        </span>


                        <button
                            disabled={
                                currentPage ===
                                totalPages ||
                                totalPages === 0
                            }
                            onClick={() =>
                                setCurrentPage(
                                    currentPage + 1
                                )
                            }
                            className="px-4 py-2 rounded-lg bg-gray-200 disabled:opacity-40"
                        >
                            Next
                        </button>

                    </div>

                </div>

            </div>


            {/* =================================================
                VIEW MODAL
            ================================================= */}

            {showViewModal &&
                selectedStudent && (

                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">

                        <div className="bg-white rounded-3xl p-8 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">

                            <div className="flex justify-between items-center mb-6">

                                <div>

                                    <h2 className="text-3xl font-bold">
                                        Student Profile
                                    </h2>

                                    <p className="text-gray-500 mt-1">
                                        {selectedStudent.admission_no}
                                    </p>

                                </div>

                                <button
                                    onClick={() =>
                                        setShowViewModal(
                                            false
                                        )
                                    }
                                    className="text-red-500 text-xl font-bold"
                                >
                                    ✕
                                </button>

                            </div>


                            {/* Status */}

                            <div className="mb-6">

                                <span
                                    className={`px-4 py-2 rounded-full text-sm font-bold ${selectedStudent.status ===
                                            "Inactive"
                                            ? "bg-gray-200 text-gray-700"
                                            : "bg-green-100 text-green-700"
                                        }`}
                                >

                                    {selectedStudent.status ===
                                        "Inactive"
                                        ? "Inactive Student"
                                        : "Active Student"}

                                </span>

                            </div>


                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                                <div>
                                    <p className="text-gray-500 text-sm">
                                        Admission No
                                    </p>

                                    <p className="font-semibold">
                                        {
                                            selectedStudent.admission_no
                                        }
                                    </p>
                                </div>


                                <div>
                                    <p className="text-gray-500 text-sm">
                                        Student Name
                                    </p>

                                    <p className="font-semibold">
                                        {
                                            selectedStudent.name
                                        }
                                    </p>
                                </div>


                                <div>
                                    <p className="text-gray-500 text-sm">
                                        Date of Birth
                                    </p>

                                    <p className="font-semibold">
                                        {
                                            selectedStudent.dob
                                                ? new Date(
                                                    selectedStudent.dob
                                                ).toLocaleDateString(
                                                    "en-GB"
                                                )
                                                : "-"
                                        }
                                    </p>
                                </div>


                                <div>
                                    <p className="text-gray-500 text-sm">
                                        Gender
                                    </p>

                                    <p className="font-semibold">
                                        {
                                            selectedStudent.gender ||
                                            "-"
                                        }
                                    </p>
                                </div>


                                <div>
                                    <p className="text-gray-500 text-sm">
                                        Class
                                    </p>

                                    <p className="font-semibold">
                                        {
                                            selectedStudent.class ||
                                            "-"
                                        }
                                        -
                                        {
                                            selectedStudent.section ||
                                            "-"
                                        }
                                    </p>
                                </div>


                                <div>
                                    <p className="text-gray-500 text-sm">
                                        Father Name
                                    </p>

                                    <p className="font-semibold">
                                        {
                                            selectedStudent.father_name ||
                                            "-"
                                        }
                                    </p>
                                </div>


                                <div>
                                    <p className="text-gray-500 text-sm">
                                        Mother Name
                                    </p>

                                    <p className="font-semibold">
                                        {
                                            selectedStudent.mother_name ||
                                            "-"
                                        }
                                    </p>
                                </div>


                                <div>
                                    <p className="text-gray-500 text-sm">
                                        Parent Mobile
                                    </p>

                                    <p className="font-semibold">
                                        {
                                            selectedStudent.parent_mobile ||
                                            "-"
                                        }
                                    </p>
                                </div>


                                <div>
                                    <p className="text-gray-500 text-sm">
                                        Emergency Contact
                                    </p>

                                    <p className="font-semibold">
                                        {
                                            selectedStudent.emergency_contact ||
                                            "-"
                                        }
                                    </p>
                                </div>


                                <div>
                                    <p className="text-gray-500 text-sm">
                                        Blood Group
                                    </p>

                                    <p className="font-semibold">
                                        {
                                            selectedStudent.blood_group ||
                                            "-"
                                        }
                                    </p>
                                </div>

                            </div>


                            <div className="mt-6">

                                <p className="text-gray-500 text-sm">
                                    Address
                                </p>

                                <p className="font-semibold mt-1">
                                    {
                                        selectedStudent.address ||
                                        "-"
                                    }
                                </p>

                            </div>


                            <div className="mt-6 flex justify-end">

                                <button
                                    onClick={() =>
                                        setShowViewModal(
                                            false
                                        )
                                    }
                                    className="bg-gray-800 hover:bg-gray-900 text-white px-6 py-3 rounded-xl font-semibold"
                                >
                                    Close
                                </button>

                            </div>

                        </div>

                    </div>

                )}


            {/* =================================================
                EDIT MODAL
            ================================================= */}

            {showEditModal &&
                selectedStudent && (

                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">

                        <div className="bg-white rounded-3xl p-8 w-full max-w-3xl shadow-2xl max-h-[90vh] overflow-y-auto">

                            <div className="flex justify-between items-center mb-6">

                                <div>

                                    <h2 className="text-3xl font-bold">
                                        Edit Student
                                    </h2>

                                    <p className="text-gray-500 mt-1">
                                        {
                                            selectedStudent.admission_no
                                        }
                                    </p>

                                </div>

                                <button
                                    onClick={() => {

                                        setShowEditModal(
                                            false
                                        );

                                        setSelectedStudent(
                                            null
                                        );

                                    }}
                                    className="text-red-500 text-xl font-bold"
                                >
                                    ✕
                                </button>

                            </div>


                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                {/* Student Name */}

                                <div>

                                    <label className="block mb-2 font-semibold text-gray-700">
                                        Student Name
                                    </label>

                                    <input
                                        placeholder="Enter student full name"
                                        value={
                                            selectedStudent.name ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            setSelectedStudent({
                                                ...selectedStudent,
                                                name: e.target.value
                                            })
                                        }
                                        className="border p-3 rounded-xl w-full"
                                    />

                                </div>


                                {/* DOB */}

                                <div>

                                    <label className="block mb-2 font-semibold text-gray-700">
                                        Date of Birth
                                    </label>

                                    <input
                                        type="date"
                                        value={
                                            selectedStudent.dob ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            setSelectedStudent({
                                                ...selectedStudent,
                                                dob: e.target.value
                                            })
                                        }
                                        className="border p-3 rounded-xl w-full"
                                    />

                                </div>


                                {/* Gender */}

                                <div>

                                    <label className="block mb-2 font-semibold text-gray-700">
                                        Gender
                                    </label>

                                    <select
                                        value={
                                            selectedStudent.gender ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            setSelectedStudent({
                                                ...selectedStudent,
                                                gender: e.target.value
                                            })
                                        }
                                        className="border p-3 rounded-xl w-full"
                                    >

                                        <option value="">
                                            Select Gender
                                        </option>

                                        <option value="Male">
                                            Male
                                        </option>

                                        <option value="Female">
                                            Female
                                        </option>

                                        <option value="Other">
                                            Other
                                        </option>

                                    </select>

                                </div>


                                {/* Class */}

                                <div>

                                    <label className="block mb-2 font-semibold text-gray-700">
                                        Class
                                    </label>

                                    <select
                                        value={
                                            selectedStudent.class ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            setSelectedStudent({
                                                ...selectedStudent,
                                                class: e.target.value
                                            })
                                        }
                                        className="border p-3 rounded-xl w-full"
                                    >

                                        <option value="">
                                            Select Class
                                        </option>

                                        {classOptions.map(
                                            (cls) => (

                                                <option
                                                    key={cls}
                                                    value={cls}
                                                >
                                                    {cls}
                                                </option>

                                            )
                                        )}

                                    </select>

                                </div>


                                {/* Section */}

                                <div>

                                    <label className="block mb-2 font-semibold text-gray-700">
                                        Section
                                    </label>

                                    <select
                                        value={
                                            selectedStudent.section ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            setSelectedStudent({
                                                ...selectedStudent,
                                                section: e.target.value
                                            })
                                        }
                                        className="border p-3 rounded-xl w-full"
                                    >

                                        <option value="">
                                            Select Section
                                        </option>

                                        {sectionOptions.map(
                                            (letter) => (

                                                <option
                                                    key={letter}
                                                    value={letter}
                                                >
                                                    {letter}
                                                </option>

                                            )
                                        )}

                                    </select>

                                </div>


                                {/* Student Status */}

                                <div>

                                    <label className="block mb-2 font-semibold text-gray-700">
                                        Student Status
                                    </label>

                                    <select
                                        value={
                                            selectedStudent.status ||
                                            "Active"
                                        }
                                        onChange={(e) =>
                                            setSelectedStudent({
                                                ...selectedStudent,
                                                status: e.target.value
                                            })
                                        }
                                        className="border p-3 rounded-xl w-full"
                                    >

                                        <option value="Active">
                                            Active
                                        </option>

                                        <option value="Inactive">
                                            Inactive
                                        </option>

                                    </select>

                                </div>


                                {/* Parent Mobile */}

                                <div>

                                    <label className="block mb-2 font-semibold text-gray-700">
                                        Parent Mobile
                                    </label>

                                    <input
                                        type="text"
                                        placeholder="Enter 10-digit mobile number"
                                        value={
                                            selectedStudent.parent_mobile ||
                                            ""
                                        }
                                        maxLength={10}
                                        onChange={(e) => {

                                            const onlyNumbers =
                                                e.target.value.replace(
                                                    /\D/g,
                                                    ""
                                                );

                                            setSelectedStudent({
                                                ...selectedStudent,
                                                parent_mobile:
                                                    onlyNumbers
                                            });

                                        }}
                                        className="border p-3 rounded-xl w-full"
                                    />

                                </div>


                                {/* Emergency Contact */}

                                <div>

                                    <label className="block mb-2 font-semibold text-gray-700">
                                        Emergency Contact
                                    </label>

                                    <input
                                        type="text"
                                        placeholder="Enter emergency contact number"
                                        value={
                                            selectedStudent.emergency_contact ||
                                            ""
                                        }
                                        maxLength={10}
                                        onChange={(e) => {

                                            const onlyNumbers =
                                                e.target.value.replace(
                                                    /\D/g,
                                                    ""
                                                );

                                            setSelectedStudent({
                                                ...selectedStudent,
                                                emergency_contact:
                                                    onlyNumbers
                                            });

                                        }}
                                        className="border rounded-xl p-3 w-full"
                                    />

                                </div>


                                {/* Father */}

                                <div>

                                    <label className="block mb-2 font-semibold text-gray-700">
                                        Father Name
                                    </label>

                                    <input
                                        placeholder="Enter father name"
                                        value={
                                            selectedStudent.father_name ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            setSelectedStudent({
                                                ...selectedStudent,
                                                father_name:
                                                    e.target.value
                                            })
                                        }
                                        className="border p-3 rounded-xl w-full"
                                    />

                                </div>


                                {/* Mother */}

                                <div>

                                    <label className="block mb-2 font-semibold text-gray-700">
                                        Mother Name
                                    </label>

                                    <input
                                        placeholder="Enter mother name"
                                        value={
                                            selectedStudent.mother_name ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            setSelectedStudent({
                                                ...selectedStudent,
                                                mother_name:
                                                    e.target.value
                                            })
                                        }
                                        className="border p-3 rounded-xl w-full"
                                    />

                                </div>

                            </div>


                            {/* Address */}

                            <div className="mt-4">

                                <label className="block mb-2 font-semibold text-gray-700">
                                    Address
                                </label>

                                <textarea
                                    rows="3"
                                    placeholder="Enter complete address"
                                    value={
                                        selectedStudent.address ||
                                        ""
                                    }
                                    onChange={(e) =>
                                        setSelectedStudent({
                                            ...selectedStudent,
                                            address:
                                                e.target.value
                                        })
                                    }
                                    className="border p-3 rounded-xl w-full"
                                />

                            </div>


                            {/* Buttons */}

                            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">

                                <button
                                    onClick={() => {

                                        setShowEditModal(
                                            false
                                        );

                                        setSelectedStudent(
                                            null
                                        );

                                    }}
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-xl font-semibold"
                                >
                                    Cancel
                                </button>


                                <button
                                    onClick={
                                        handleUpdateStudent
                                    }
                                    className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-3 rounded-xl font-bold"
                                >
                                    Update Student
                                </button>

                            </div>

                        </div>

                    </div>

                )}

        </div>
    );
}